import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  Stack,
  Button,
  CircularProgress,
  Pagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import logoMatches from "../../static/Home/logo_matches.webp";

const ITEMS_PER_PAGE = 6;

export default function SchedulePage() {
  const navigate = useNavigate();

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().slice(0, 10);

  const [selectedDate, setSelectedDate] = useState(today);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      const res = await fetch("/matches/public/");
      const data = await res.json();

      if (Array.isArray(data)) {
        setMatches(data);
      } else {
        setMatches([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const availableDates = useMemo(() => {
    const unique = [...new Set(matches.map(m => m.date))];
    return unique.sort();
  }, [matches]);

  useEffect(() => {
    if (!availableDates.includes(selectedDate) && availableDates.length) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates]);

  const filteredMatches = useMemo(() => {
    return matches
      .filter(match => match.date === selectedDate)
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [matches, selectedDate]);

  const paginatedMatches = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredMatches.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMatches, page]);

  const totalPages = Math.ceil(filteredMatches.length / ITEMS_PER_PAGE);

  const formatDateButton = date => {
    const d = new Date(date + "T12:00:00");

    const weekday = d.toLocaleDateString("en-US", {
      weekday: "short",
    });

    const day = d.getDate();

    return `${weekday.toUpperCase()} ${day}`;
  };

  const getScore = (match, side) => {
    if (match.status === "scheduled") return "-";

    return side === "local"
      ? match.score_local ?? 0
      : match.score_visitor ?? 0;
  };

  const getStatusLabel = status => {
    if (status === "live") return "LIVE";
    if (status === "finished") return "FINAL";
    if (status === "scheduled") return "SCHEDULED";

    return "CANCELLED";
  };

  const getStatusColor = status => {
    if (status === "live") return "#d32f2f";
    if (status === "finished") return "#1976d2";

    return "#900028";
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Typography variant="h1">
        Match Schedule
      </Typography>

      {loading ? (
        <Stack alignItems="center" sx={{ mt: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              mt: 4,
              mb: 5,
              overflowX: "auto",
              pb: 1,
            }}
          >
            {availableDates.map(date => (
              <Button
                key={date}
                variant={
                  selectedDate === date
                    ? "contained"
                    : "outlined"
                }
                onClick={() => {
                  setSelectedDate(date);
                  setPage(1);
                }}
                sx={{
                  minWidth: 95,
                  fontWeight: 700,
                }}
              >
                {formatDateButton(date)}
              </Button>
            ))}
          </Stack>

          {filteredMatches.length === 0 ? (
            <Typography align="center">
              No matches scheduled
            </Typography>
          ) : (
            <>
              <Stack spacing={3}>
                {paginatedMatches.map(match => (
                  <Card
                    key={match.id}
                    sx={{
                      borderRadius: 4,
                      p: 3,
                      boxShadow: "0 18px 40px rgba(0,0,0,0.10)",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack
                        alignItems="center"
                        spacing={1}
                        sx={{ width: 160 }}
                      >
                        <Box
                          component="img"
                          src={match.team_local_logo}
                          sx={{
                            width: 74,
                            height: 74,
                            objectFit: "contain",
                          }}
                        />

                        <Typography fontWeight={800}>
                          {match.team_local}
                        </Typography>
                      </Stack>

                      <Stack alignItems="center" spacing={1.2}>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            color: getStatusColor(match.status),
                          }}
                        >
                          {getStatusLabel(match.status)}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1.2}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              minWidth: 72,
                              height: 74,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "16px",
                              background:
                                "linear-gradient(180deg,#ffffff 0%,#f5f8fb 100%)",
                              border:
                                "1px solid rgba(8,112,162,0.14)",
                              boxShadow:
                                "0 10px 24px rgba(8,112,162,0.08)",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 900,
                                fontSize: "2.7rem",
                                color: "primary.main",
                              }}
                            >
                              {getScore(match, "local")}
                            </Typography>
                          </Box>

                          <Box
                            component="img"
                            src={logoMatches}
                            sx={{
                              width: 34,
                              height: 34,
                            }}
                          />

                          <Box
                            sx={{
                              minWidth: 72,
                              height: 74,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "16px",
                              background:
                                "linear-gradient(180deg,#ffffff 0%,#f5f8fb 100%)",
                              border:
                                "1px solid rgba(8,112,162,0.14)",
                              boxShadow:
                                "0 10px 24px rgba(8,112,162,0.08)",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 900,
                                fontSize: "2.7rem",
                                color: "primary.main",
                              }}
                            >
                              {getScore(match, "visitor")}
                            </Typography>
                          </Box>
                        </Stack>

                        <Typography>
                          {match.time} · {match.venue}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "text.secondary",
                          }}
                        >
                          {match.category}
                        </Typography>
                      </Stack>

                      <Stack
                        alignItems="center"
                        spacing={1}
                        sx={{ width: 160 }}
                      >
                        <Box
                          component="img"
                          src={match.team_visitor_logo}
                          sx={{
                            width: 74,
                            height: 74,
                            objectFit: "contain",
                          }}
                        />

                        <Typography fontWeight={800}>
                          {match.team_visitor}
                        </Typography>
                      </Stack>
                    </Stack>

                    {match.status === "live" && (
                      <Stack alignItems="center" sx={{ mt: 3 }}>
                        <Button
                          variant="contained"
                          onClick={() =>
                            navigate(`/partido/${match.id}`)
                          }
                        >
                          Watch Live
                        </Button>
                      </Stack>
                    )}
                  </Card>
                ))}
              </Stack>

              {totalPages > 1 && (
                <Stack alignItems="center" sx={{ mt: 5 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) =>
                      setPage(value)
                    }
                    color="primary"
                  />
                </Stack>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
}